import type { PageHelp } from '../../help/types';

/** Page guide for `/projects/:code/keys` — the editor (spec §9.3, §9.5). */
export const keysHelp: PageHelp = {
  title: 'Keys',
  intro:
    'The editor for one project: one row per key, the source locale plus up to six locale columns, and inline editing that saves as you go. Releases publish what is written here.',
  sections: [
    'Getting around',
    'Finding a key',
    'Reading the grid',
    'Working on a selection',
    'Save conflicts',
    'Key details',
    'New key',
    'Rename a key',
    'QA',
    'Export',
    'AI translate',
  ],
  contexts: {
    selection: 'Tick the checkbox at the start of one or more rows.',
    'tag-dialog': 'Select rows, then click “Add tag” in the selection bar.',
    'conflict-dialog': 'Opens by itself when a save collides with a value somebody else saved first.',
    'detail-drawer': 'Click a key name in the grid.',
    'status-filter': 'Pick a value in the “Status” filter first.',
    'rename-dialog': 'Open a key and click the pencil icon next to its name.',
    'new-key-dialog': 'Click “New key” at the top right.',
    'qa-dialog': 'Click “QA” at the top, or “Run QA” in the selection bar.',
    'export-dialog': 'Click “Export” at the top, or “Export selection” in the selection bar.',
    'export-release': 'In the Export dialog, set “From” to “The last release of a target”.',
    'ai-dialog': 'Click “AI translate all” at the top, or “AI translate” in the selection bar.',
    'ai-running': 'Click “Translate” in the AI translate dialog.',
  },
  tips: [
    'What you may change depends on your role in this project. Owners and translators edit values and mark them reviewed; owners and developers create, rename, tag and delete keys; administrators may do everything. Anyone who can open the project may comment.',
    'A key with no value in the source locale is not exported in any locale, and an untranslated cell is left out of the published file, so the application shows its fallback text there.',
    'If a save tells you a change became a suggestion, the key belongs to someone else: a shared package you do not own, or another project whose key this one links and in which you do not translate. That owner decides on it; nothing was written directly.',
    'Keys this project links from other projects are listed with their owner’s code next to the name, after a link is made on Linked keys or from the missing-keys inbox; add ?includeLinked=0 to the address to hide them. Their rows cannot be ticked, because deleting, tagging and reviewing a key are its owner’s.',
  ],
  steps: [
    // ----- Getting around -----
    {
      selector: '[data-help="keys-new"]',
      title: 'Create a key',
      body: 'Opens the New key dialog for a name, an optional description and the source text. The server accepts it only from the owner or developer role (administrators always), and only for a prefix this project owns. A key with a package’s prefix is created on that package’s Keys page (Packages → Keys).',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Einen Schlüssel anlegen',
          body: 'Öffnet den Dialog „New key“ für Namen, optionale Beschreibung und Quelltext. Der Server nimmt ihn nur von den Rollen Owner oder Developer an (Administratoren immer) und nur für ein Präfix, das diesem Projekt gehört. Ein Schlüssel mit dem Präfix eines Pakets wird auf der Schlüsselseite dieses Pakets angelegt (Packages → Keys).',
        },
        'sr-Latn-RS': {
          title: 'Kreirajte ključ',
          body: 'Otvara dijalog „New key“ za naziv, opcioni opis i izvorni tekst. Server ga prihvata samo od uloga owner ili developer (administratori uvek) i samo za prefiks koji pripada ovom projektu. Ključ sa prefiksom paketa kreira se na stranici Keys tog paketa (Packages → Keys).',
        },
      },
    },
    {
      selector: '[data-help="keys-save"]',
      title: 'Save pending edits',
      body: 'A cell saves itself when you leave it, so this button only counts what has not reached the server yet — the cell you are typing in, or a save that failed. It sends all of them in one batch, like Ctrl+S; if somebody saved one of those cells first, nothing is written and the conflict dialog opens.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Offene Änderungen speichern',
          body: 'Eine Zelle speichert sich selbst, sobald Sie sie verlassen; dieser Button zählt daher nur, was den Server noch nicht erreicht hat — die Zelle, in der Sie gerade tippen, oder ein fehlgeschlagenes Speichern. Er sendet alles in einem Durchgang, wie Strg+S; hat jemand eine dieser Zellen vorher gespeichert, wird nichts geschrieben und der Konfliktdialog öffnet sich.',
        },
        'sr-Latn-RS': {
          title: 'Sačuvajte nesačuvane izmene',
          body: 'Ćelija se sama čuva kada je napustite, pa ovo dugme broji samo ono što još nije stiglo do servera — ćeliju u kojoj kucate ili čuvanje koje nije uspelo. Šalje sve odjednom, kao Ctrl+S; ako je neko pre vas sačuvao neku od tih ćelija, ništa se ne upisuje i otvara se dijalog za konflikt.',
        },
      },
    },
    {
      selector: '[data-help="keys-ai-translate-all"]',
      title: 'Machine-translate the project',
      body: 'Switched off for now: the button stays greyed out until AI translation is available. Opens the AI translate dialog for every key that has a source value — or only for the ticked rows when some are selected. The job never overwrites draft or reviewed text. Owners, translators and developers may start it.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Das Projekt maschinell übersetzen',
          body: 'Derzeit abgeschaltet: Die Schaltfläche bleibt ausgegraut, bis die KI-Übersetzung verfügbar ist. Öffnet den Dialog „AI translate“ für jeden Schlüssel mit Quellwert — oder nur für die angehakten Zeilen, wenn welche ausgewählt sind. Der Auftrag überschreibt nie Texte im Status „draft“ oder „reviewed“. Owner, Translator und Developer dürfen ihn starten.',
        },
        'sr-Latn-RS': {
          title: 'Mašinski prevedite projekat',
          body: 'Trenutno isključeno: dugme ostaje sivo dok AI prevod ne postane dostupan. Otvara dijalog „AI translate“ za svaki ključ koji ima izvornu vrednost — ili samo za označene redove, ako ih ima. Posao nikada ne prepisuje tekst sa statusom „draft“ ili „reviewed“. Mogu da ga pokrenu owner, translator i developer.',
        },
      },
    },
    {
      selector: '[data-help="keys-qa"]',
      title: 'Check stored translations',
      body: 'Opens the QA dialog for the whole project — or only for the ticked rows when some are selected — one locale at a time. It reads what is stored and writes nothing, so it is safe to run whenever you like.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Gespeicherte Übersetzungen prüfen',
          body: 'Öffnet den QA-Dialog für das ganze Projekt — oder nur für die angehakten Zeilen, wenn welche ausgewählt sind — eine Sprache nach der anderen. Er liest nur, was gespeichert ist, und schreibt nichts; Sie können ihn also jederzeit ausführen.',
        },
        'sr-Latn-RS': {
          title: 'Proverite sačuvane prevode',
          body: 'Otvara QA dijalog za ceo projekat — ili samo za označene redove, ako ih ima — jedan po jedan jezik. Samo čita ono što je sačuvano i ništa ne upisuje, pa ga možete pokrenuti kad god želite.',
        },
      },
    },
    {
      selector: '[data-help="keys-export"]',
      title: 'Download translation files',
      body: 'Opens the Export dialog: one locale or every locale, from the current draft or from what a target last published. With rows ticked it also offers a working file of just those keys.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Übersetzungsdateien herunterladen',
          body: 'Öffnet den Dialog „Export“: eine Sprache oder alle Sprachen, aus dem aktuellen Entwurf oder aus dem, was ein Ziel zuletzt veröffentlicht hat. Mit angehakten Zeilen gibt es zusätzlich eine Arbeitsdatei nur mit diesen Schlüsseln.',
        },
        'sr-Latn-RS': {
          title: 'Preuzmite fajlove sa prevodima',
          body: 'Otvara dijalog „Export“: jedan jezik ili svi jezici, iz trenutnog nacrta ili iz onoga što je neki cilj poslednji put objavio. Ako su redovi označeni, nudi i radni fajl samo sa tim ključevima.',
        },
      },
    },

    // ----- Finding a key -----
    {
      selector: '[data-help="keys-search"]',
      title: 'Search keys and values',
      body: 'Matches key names, descriptions and values in any locale, ignoring case. The list updates a moment after you stop typing.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Schlüssel und Werte durchsuchen',
          body: 'Findet Schlüsselnamen, Beschreibungen und Werte in jeder Sprache, ohne Rücksicht auf Groß- und Kleinschreibung. Die Liste aktualisiert sich kurz nachdem Sie aufgehört haben zu tippen.',
        },
        'sr-Latn-RS': {
          title: 'Pretražite ključeve i vrednosti',
          body: 'Pronalazi nazive ključeva, opise i vrednosti na bilo kom jeziku, bez obzira na velika i mala slova. Lista se osvežava trenutak nakon što prestanete da kucate.',
        },
      },
    },
    {
      selector: '[data-help="keys-filters"]',
      title: 'Share a filtered view',
      body: 'Search and filters are kept in the page address, so a filtered view is a link you can send to someone. The number on the right counts the keys that match; while not all of them are loaded it reads "100 of 2296 keys".',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Eine gefilterte Ansicht teilen',
          body: 'Suche und Filter stehen in der Adresse der Seite, eine gefilterte Ansicht ist also ein Link, den Sie weitergeben können. Die Zahl rechts zählt die passenden Schlüssel; solange nicht alle geladen sind, steht dort etwa „100 of 2296 keys“.',
        },
        'sr-Latn-RS': {
          title: 'Podelite filtrirani prikaz',
          body: 'Pretraga i filteri čuvaju se u adresi stranice, pa je filtrirani prikaz link koji možete nekome poslati. Broj desno pokazuje koliko ključeva odgovara; dok nisu svi učitani, piše npr. „100 of 2296 keys“.',
        },
      },
    },
    {
      selector: '[data-help="keys-missing-in"]',
      title: 'Find what a locale still needs',
      body: 'Shows only keys that have no value in the chosen locale — the fastest way to see what a language still needs. Show the same locale as a column and you can fill the gaps in place. Type to find a locale; clear the field to see every key again.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Finden, was einer Sprache noch fehlt',
          body: 'Zeigt nur Schlüssel ohne Wert in der gewählten Sprache — der schnellste Weg zu sehen, was einer Sprache noch fehlt. Blenden Sie dieselbe Sprache als Spalte ein, dann können Sie die Lücken direkt füllen. Tippen Sie, um eine Sprache zu finden; leeren Sie das Feld, um wieder alle Schlüssel zu sehen.',
        },
        'sr-Latn-RS': {
          title: 'Pronađite šta jeziku još nedostaje',
          body: 'Prikazuje samo ključeve koji nemaju vrednost na izabranom jeziku — najbrži način da vidite šta jeziku još nedostaje. Prikažite isti jezik kao kolonu i praznine možete popuniti na licu mesta. Kucajte da pronađete jezik; ispraznite polje da ponovo vidite sve ključeve.',
        },
      },
    },
    {
      selector: '[data-help="keys-include-packages"]',
      title: 'Show the keys of the packages',
      body: 'Adds the keys of the packages this project uses to the list; they are part of its published files, and the dashboard counts them in its coverage. Off by default, because shared packages hold thousands of keys. A package key is listed under “Missing in” only for locales its package translates. An edit to one is written directly only if you own that package; otherwise it becomes a suggestion for the package owner. Not shown on a package’s own Keys page.',
      section: 'Finding a key',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Schlüssel der Pakete anzeigen',
          body: 'Nimmt die Schlüssel der Pakete, die dieses Projekt verwendet, in die Liste auf; sie gehören zu seinen veröffentlichten Dateien, und das Dashboard zählt sie in seiner Abdeckung mit. Standardmäßig aus, weil gemeinsame Pakete Tausende Schlüssel enthalten. Unter „Missing in“ erscheint ein Paketschlüssel nur für Sprachen, die sein Paket übersetzt. Eine Änderung daran wird nur dann direkt geschrieben, wenn Sie Owner dieses Pakets sind; sonst wird sie zu einem Vorschlag für den Owner des Pakets. Auf der Schlüsselseite eines Pakets selbst nicht vorhanden.',
        },
        'sr-Latn-RS': {
          title: 'Prikažite ključeve paketa',
          body: 'Dodaje u listu ključeve paketa koje ovaj projekat koristi; oni su deo njegovih objavljenih fajlova, a dashboard ih računa u pokrivenosti. Podrazumevano je isključeno, jer zajednički paketi imaju hiljade ključeva. Pod „Missing in“ ključ paketa se prikazuje samo za jezike koje njegov paket prevodi. Izmena takvog ključa upisuje se direktno samo ako ste vlasnik tog paketa; inače postaje predlog za vlasnika paketa. Ne postoji na stranici Keys samog paketa.',
        },
      },
    },
    {
      selector: '[data-help="keys-status"]',
      title: 'Filter by translation status',
      body: 'Shows keys whose value has the chosen status in one locale: empty, machine (written by AI), draft or reviewed. Once a status is picked, "Status in" appears next to it to choose that locale.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Nach Übersetzungsstatus filtern',
          body: 'Zeigt Schlüssel, deren Wert in einer Sprache den gewählten Status hat: „Empty“, „Machine“ (von der KI geschrieben), „Draft“ oder „Reviewed“. Sobald ein Status gewählt ist, erscheint daneben „Status in“ zur Wahl dieser Sprache.',
        },
        'sr-Latn-RS': {
          title: 'Filtrirajte po statusu prevoda',
          body: 'Prikazuje ključeve čija vrednost na jednom jeziku ima izabrani status: „Empty“, „Machine“ (napisao AI), „Draft“ ili „Reviewed“. Kad izaberete status, pored njega se pojavljuje „Status in“ za izbor tog jezika.',
        },
      },
    },
    {
      selector: '[data-help="keys-status-locale"]',
      title: 'Choose the locale the status is read in',
      body: 'The status filter looks at this one locale. It starts on the first locale column you are showing, because the question is usually about a translation ("what is still machine in de-AT?"); pick the source locale to check source texts instead.',
      section: 'Finding a key',
      context: 'status-filter',
      i18n: {
        'de-AT': {
          title: 'Die Sprache für den Status wählen',
          body: 'Der Statusfilter betrachtet genau diese eine Sprache. Er beginnt mit der ersten eingeblendeten Sprachspalte, weil die Frage meist einer Übersetzung gilt („was ist in de-AT noch maschinell?“); wählen Sie die Quellsprache, um die Quelltexte zu prüfen.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite jezik za koji se čita status',
          body: 'Filter statusa gleda samo ovaj jedan jezik. Počinje od prve prikazane kolone jezika, jer se pitanje obično tiče prevoda („šta je još mašinsko na de-AT?“); izaberite izvorni jezik ako želite da proverite izvorne tekstove.',
        },
      },
    },
    {
      selector: '[data-help="keys-locales"]',
      title: 'Choose the locale columns',
      body: 'Pick up to six locales to show next to the source locale, which is always the first column. The choice is remembered in this browser, not per project; until you pick, the grid shows the first other locale of the project. Type to find a locale; the × on a locale removes its column.',
      section: 'Finding a key',
      i18n: {
        'de-AT': {
          title: 'Die Sprachspalten wählen',
          body: 'Wählen Sie bis zu sechs Sprachen, die neben der Quellsprache angezeigt werden; die Quellsprache ist immer die erste Spalte. Die Auswahl merkt sich dieser Browser, nicht das Projekt; solange Sie nichts wählen, zeigt das Raster die erste weitere Sprache des Projekts. Tippen Sie, um eine Sprache zu finden; das × an einer Sprache entfernt ihre Spalte.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite kolone jezika',
          body: 'Izaberite do šest jezika koji se prikazuju pored izvornog jezika, koji je uvek prva kolona. Izbor pamti ovaj pregledač, a ne projekat; dok ništa ne izaberete, tabela prikazuje prvi sledeći jezik projekta. Kucajte da pronađete jezik; × na jeziku uklanja njegovu kolonu.',
        },
      },
    },

    // ----- Reading the grid -----
    {
      selector: '[data-help="keys-key-column"]',
      title: 'Read the key column',
      body: 'One row per key, in the order the keys were created. A list icon marks a plural key, a speech bubble a key with a description and a tag icon a key with tags — hover an icon to read them.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Schlüsselspalte lesen',
          body: 'Eine Zeile pro Schlüssel, in der Reihenfolge, in der die Schlüssel angelegt wurden. Ein Listensymbol kennzeichnet einen Pluralschlüssel, eine Sprechblase einen Schlüssel mit Beschreibung und ein Etikettsymbol einen Schlüssel mit Tags — fahren Sie mit der Maus über ein Symbol, um sie zu lesen.',
        },
        'sr-Latn-RS': {
          title: 'Čitajte kolonu ključeva',
          body: 'Jedan red po ključu, redosledom kojim su ključevi kreirani. Ikonica liste označava ključ sa množinom, oblačić ključ koji ima opis, a ikonica etikete ključ sa tagovima — zadržite pokazivač iznad ikonice da biste ih pročitali.',
        },
      },
    },
    {
      selector: '[data-help="keys-key-name"]',
      title: 'Open a key',
      body: 'Click the key name to open its details on the right: every locale at once, plural forms, comments, history and the routes it was seen on. A row marked “edited — no longer matches the filter” is one you changed that the current filter would otherwise hide.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen Schlüssel öffnen',
          body: 'Klicken Sie auf den Schlüsselnamen, um rechts die Details zu öffnen: alle Sprachen auf einmal, Pluralformen, Kommentare, Verlauf und die Routen, auf denen er gesehen wurde. Eine Zeile mit „edited — no longer matches the filter“ haben Sie geändert; der aktuelle Filter würde sie sonst ausblenden.',
        },
        'sr-Latn-RS': {
          title: 'Otvorite ključ',
          body: 'Kliknite na naziv ključa da biste desno otvorili detalje: sve jezike odjednom, oblike množine, komentare, istoriju i rute na kojima je viđen. Red sa oznakom „edited — no longer matches the filter“ je onaj koji ste izmenili, a koji bi trenutni filter inače sakrio.',
        },
      },
    },
    {
      selector: '[data-help="keys-owner-badge"]',
      title: 'Whose key a row is',
      body: 'A code next to the name means the key belongs to that project or package, not to this one — typically a key this project links. Its text comes from the owner, and an edit is written there only if you translate there; otherwise it becomes a suggestion for the owner. The row cannot be ticked.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Wem ein Schlüssel gehört',
          body: 'Ein Code neben dem Namen bedeutet, dass der Schlüssel diesem Projekt oder Paket gehört, nicht diesem hier – meist ein Schlüssel, den dieses Projekt verknüpft. Sein Text kommt vom Owner, und eine Änderung wird dort nur geschrieben, wenn Sie dort übersetzen; sonst wird sie zu einem Vorschlag für den Owner. Die Zeile lässt sich nicht anhaken.',
        },
        'sr-Latn-RS': {
          title: 'Čiji je ključ u redu',
          body: 'Kod pored imena znači da ključ pripada tom projektu ili paketu, a ne ovom – obično je to ključ koji ovaj projekat povezuje. Tekst dolazi od vlasnika, a izmena se tamo upisuje samo ako tamo prevodite; inače postaje predlog za vlasnika. Red ne može da se označi.',
        },
      },
    },
    {
      selector: '[data-help="keys-source-column"]',
      title: 'Edit the source text',
      body: 'The source locale’s value is what every other locale is translated from, and what placeholders and lengths are compared against; its column is always first and lightly shaded, and an empty cell says “Add source text”. A key with no source value is not exported in any locale.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Quelltext bearbeiten',
          body: 'Aus dem Wert der Quellsprache wird in alle anderen Sprachen übersetzt, und mit ihm werden Platzhalter und Längen verglichen; seine Spalte steht immer an erster Stelle, leicht hinterlegt, und eine leere Zelle zeigt „Add source text“. Ein Schlüssel ohne Quellwert wird in keiner Sprache exportiert.',
        },
        'sr-Latn-RS': {
          title: 'Uredite izvorni tekst',
          body: 'Sa vrednosti izvornog jezika prevodi se na sve ostale jezike i sa njom se porede placeholderi i dužine; njena kolona je uvek prva i blago osenčena, a prazna ćelija prikazuje „Add source text“. Ključ bez izvorne vrednosti ne izvozi se ni na jednom jeziku.',
        },
      },
    },
    {
      selector: '[data-help="keys-locale-column"]',
      title: 'Translate in a locale column',
      body: 'One column per locale you chose, sharing the width of the grid; an empty cell you may edit says “Add translation”, a read-only one shows a dash. Plural keys are read-only here — open the key to fill each plural form.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'In einer Sprachspalte übersetzen',
          body: 'Eine Spalte pro gewählter Sprache, die sich die Breite des Rasters teilen; eine leere Zelle, die Sie bearbeiten dürfen, zeigt „Add translation“, eine schreibgeschützte einen Gedankenstrich. Pluralschlüssel sind hier schreibgeschützt — öffnen Sie den Schlüssel, um jede Pluralform auszufüllen.',
        },
        'sr-Latn-RS': {
          title: 'Prevodite u koloni jezika',
          body: 'Jedna kolona po izabranom jeziku; kolone dele širinu tabele. Prazna ćelija koju možete uređivati prikazuje „Add translation“, a ona samo za čitanje crticu. Ključevi sa množinom ovde su samo za čitanje — otvorite ključ da biste popunili svaki oblik množine.',
        },
      },
    },
    {
      selector: '[data-help="keys-cell"]',
      title: 'Edit a cell',
      body: 'Click into a cell and type; Enter or leaving the cell saves it, Shift+Enter adds a line break and Escape throws the edit away. The stripe on the left shows the status, a blue outline marks unsaved text and an amber one placeholders such as {{name}} that differ from the source. While you type, a counter shows the length and its ratio to the source; afterwards it stays only when the text is too long — red past the key’s maximum length, amber when it is much longer than the source. Only owners and translators can edit; for everyone else the cells are read-only.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Eine Zelle bearbeiten',
          body: 'Klicken Sie in eine Zelle und tippen Sie; Enter oder das Verlassen der Zelle speichert, Umschalt+Enter fügt einen Zeilenumbruch ein und Escape verwirft die Änderung. Der Streifen links zeigt den Status, ein blauer Rahmen ungespeicherten Text und ein bernsteinfarbener Platzhalter wie {{name}}, die vom Quelltext abweichen. Während Sie tippen, zeigt ein Zähler die Länge und das Verhältnis zum Quelltext; danach bleibt er nur, wenn der Text zu lang ist — rot über der Maximallänge des Schlüssels, bernsteinfarben, wenn er deutlich länger als der Quelltext ist. Bearbeiten können nur Owner und Translator; für alle anderen sind die Zellen schreibgeschützt.',
        },
        'sr-Latn-RS': {
          title: 'Uredite ćeliju',
          body: 'Kliknite u ćeliju i kucajte; Enter ili napuštanje ćelije je čuva, Shift+Enter dodaje novi red, a Escape odbacuje izmenu. Traka levo pokazuje status, plavi okvir označava nesačuvan tekst, a narandžasti placeholdere poput {{name}} koji se razlikuju od izvornog teksta. Dok kucate, brojač pokazuje dužinu i odnos prema izvornom tekstu; posle ostaje samo kada je tekst predugačak — crveno kada pređe maksimalnu dužinu ključa, narandžasto kada je znatno duži od izvornog teksta. Uređivati mogu samo owner i translator; za sve ostale ćelije su samo za čitanje.',
        },
      },
    },
    {
      selector: '[data-help="keys-pinned"]',
      title: 'Keep editing rows the filter dropped',
      body: 'Appears when rows you are editing no longer match the filter. They stay highlighted at the bottom of the list until they are saved, so a reload cannot take text away from under you; “Refresh” reloads the list.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Zeilen weiterbearbeiten, die der Filter verliert',
          body: 'Erscheint, wenn Zeilen, die Sie gerade bearbeiten, nicht mehr zum Filter passen. Sie bleiben hervorgehoben am Ende der Liste, bis sie gespeichert sind, damit ein Neuladen Ihnen keinen Text unter den Händen wegnimmt; „Refresh“ lädt die Liste neu.',
        },
        'sr-Latn-RS': {
          title: 'Nastavite sa redovima koje je filter izbacio',
          body: 'Pojavljuje se kada redovi koje uređujete više ne odgovaraju filteru. Ostaju istaknuti na dnu liste dok se ne sačuvaju, tako da vam ponovno učitavanje ne može uzeti tekst ispod ruku; „Refresh“ ponovo učitava listu.',
        },
      },
    },
    {
      selector: '[data-help="keys-load-more"]',
      title: 'Keys load as you scroll',
      body: 'The grid loads keys fifty at a time and fetches the next batch by itself as you scroll towards the end of the list. While more match the filters than are loaded, this footer shows how many are in, and “Load more” there fetches the next batch without scrolling. Narrowing the filters is usually faster than scrolling through a large project.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Schlüssel laden beim Scrollen',
          body: 'Das Raster lädt Schlüssel in Fünfzigerschritten und holt die nächsten von selbst, sobald Sie zum Ende der Liste scrollen. Solange mehr zu den Filtern passen, als geladen sind, zeigt diese Fußzeile, wie viele es sind, und „Load more“ dort holt die nächsten ohne Scrollen. Bei einem großen Projekt ist Eingrenzen meist schneller als Durchscrollen.',
        },
        'sr-Latn-RS': {
          title: 'Ključevi se učitavaju dok skrolujete',
          body: 'Tabela učitava ključeve po pedeset i sama donosi sledeću grupu kada skrolujete ka kraju liste. Dok filterima odgovara više ključeva nego što je učitano, ovo podnožje pokazuje koliko ih je učitano, a „Load more“ tamo donosi sledeću grupu bez skrolovanja. Kod velikog projekta je sužavanje filtera obično brže od skrolovanja.',
        },
      },
    },
    {
      selector: '[data-help="keys-grid"]',
      title: 'Work from the keyboard',
      body: 'Tab moves to the next cell and saves the one you leave. Ctrl+S saves everything pending; Ctrl+Enter saves it and marks what was written reviewed — the review half needs the owner or translator role. Alt+↓ and Alt+↑ jump to the next and previous row with an untranslated cell in the visible columns, scrolling the list there if needed.',
      section: 'Reading the grid',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Mit der Tastatur arbeiten',
          body: 'Tab springt in die nächste Zelle und speichert die verlassene. Strg+S speichert alles Offene; Strg+Enter speichert es und markiert das Geschriebene als „reviewed“ — dieser Teil braucht die Rolle Owner oder Translator. Alt+↓ und Alt+↑ springen zur nächsten bzw. vorigen Zeile mit einer unübersetzten Zelle in den sichtbaren Spalten und scrollen die Liste bei Bedarf dorthin.',
        },
        'sr-Latn-RS': {
          title: 'Radite sa tastature',
          body: 'Tab prelazi u sledeću ćeliju i čuva onu koju ste napustili. Ctrl+S čuva sve nesačuvano; Ctrl+Enter to čuva i upisano označava kao „reviewed“ — taj deo zahteva ulogu owner ili translator. Alt+↓ i Alt+↑ skaču na sledeći odnosno prethodni red sa neprevedenom ćelijom u vidljivim kolonama i po potrebi skroluju listu do njega.',
        },
      },
    },

    // ----- Working on a selection -----
    {
      selector: '[data-help="keys-row-select"]',
      title: 'Select rows',
      body: 'Tick rows to act on them together. The selection bar appears above the grid as soon as one row is ticked, and the header buttons “AI translate all” and “QA” then work on the ticked keys only.',
      section: 'Working on a selection',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Zeilen auswählen',
          body: 'Haken Sie Zeilen an, um gemeinsam mit ihnen zu arbeiten. Sobald eine Zeile angehakt ist, erscheint über dem Raster die Auswahlleiste, und die Buttons „AI translate all“ und „QA“ oben wirken dann nur auf die angehakten Schlüssel.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite redove',
          body: 'Označite redove da biste radili sa njima zajedno. Čim je jedan red označen, iznad tabele se pojavljuje traka izbora, a dugmad „AI translate all“ i „QA“ na vrhu tada rade samo sa označenim ključevima.',
        },
      },
    },
    {
      selector: '[data-help="keys-select-all"]',
      title: 'Select every loaded row',
      body: 'The checkbox in the header ticks every row loaded so far — only those, not the keys still to be scrolled in — and unticks them all when every one is already ticked.',
      section: 'Working on a selection',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Alle geladenen Zeilen auswählen',
          body: 'Das Kontrollkästchen in der Kopfzeile hakt jede bisher geladene Zeile an — nur diese, nicht die Schlüssel, die erst noch nachgeladen werden — und hebt die Auswahl auf, wenn bereits alle angehakt sind.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite sve učitane redove',
          body: 'Polje za potvrdu u zaglavlju označava svaki do sada učitani red — samo njih, ne i ključeve koji tek treba da se učitaju — a kada su svi već označeni, poništava izbor.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk"]',
      title: 'Act on the selected keys',
      body: 'Shows how many keys are selected and what you can do with them: AI translate, mark reviewed, add a tag, run QA, export or delete. “Clear selection” on the right unticks every row.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Mit den ausgewählten Schlüsseln arbeiten',
          body: 'Zeigt, wie viele Schlüssel ausgewählt sind und was Sie mit ihnen tun können: KI-Übersetzung, als „reviewed“ markieren, Tag hinzufügen, QA, Export oder Löschen. „Clear selection“ rechts entfernt alle Haken.',
        },
        'sr-Latn-RS': {
          title: 'Radite sa izabranim ključevima',
          body: 'Pokazuje koliko je ključeva izabrano i šta sa njima možete da uradite: AI prevod, označavanje kao „reviewed“, dodavanje taga, QA, izvoz ili brisanje. „Clear selection“ desno uklanja sve oznake.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk-ai-translate"]',
      title: 'Machine-translate the selection',
      body: 'Switched off for now: the button stays greyed out until AI translation is available. Opens the AI translate dialog for the selected keys only. Draft and reviewed values are never touched; what the AI writes is stored with the status machine.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Die Auswahl maschinell übersetzen',
          body: 'Derzeit abgeschaltet: Die Schaltfläche bleibt ausgegraut, bis die KI-Übersetzung verfügbar ist. Öffnet den Dialog „AI translate“ nur für die ausgewählten Schlüssel. Werte im Status „draft“ oder „reviewed“ bleiben unberührt; was die KI schreibt, wird mit dem Status „machine“ gespeichert.',
        },
        'sr-Latn-RS': {
          title: 'Mašinski prevedite izbor',
          body: 'Trenutno isključeno: dugme ostaje sivo dok AI prevod ne postane dostupan. Otvara dijalog „AI translate“ samo za izabrane ključeve. Vrednosti sa statusom „draft“ ili „reviewed“ nikada se ne diraju; ono što AI napiše čuva se sa statusom „machine“.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk-review"]',
      title: 'Mark the selection reviewed',
      body: 'Marks the values of the selected keys in the locale columns you are showing as reviewed, in one go; the message afterwards says how many values changed. It needs the owner or translator role.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Die Auswahl als geprüft markieren',
          body: 'Markiert die Werte der ausgewählten Schlüssel in den eingeblendeten Sprachspalten in einem Schritt als „reviewed“; die Meldung danach nennt, wie viele Werte sich geändert haben. Dafür braucht es die Rolle Owner oder Translator.',
        },
        'sr-Latn-RS': {
          title: 'Označite izbor kao pregledan',
          body: 'Odjednom označava vrednosti izabranih ključeva u prikazanim kolonama jezika kao „reviewed“; poruka posle toga kaže koliko se vrednosti promenilo. Zahteva ulogu owner ili translator.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk-tag"]',
      title: 'Tag the selection',
      body: 'Adds one tag to every selected key; keys that already carry it are left alone. Owners and developers only — for other roles the button is disabled.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Die Auswahl taggen',
          body: 'Fügt jedem ausgewählten Schlüssel einen Tag hinzu; Schlüssel, die ihn schon haben, bleiben unverändert. Nur für Owner und Developer — für andere Rollen ist der Button deaktiviert.',
        },
        'sr-Latn-RS': {
          title: 'Dodajte tag izboru',
          body: 'Dodaje jedan tag svakom izabranom ključu; ključevi koji ga već imaju ostaju nepromenjeni. Samo za owner i developer — za ostale uloge dugme je onemogućeno.',
        },
      },
    },
    {
      selector: '[data-help="keys-tag-name"]',
      title: 'Pick or type a tag',
      body: 'Type a new tag, or click one of the tags the keys in the list already use. Reusing an existing tag avoids a near-duplicate created by a typo; “Add tag” applies it.',
      section: 'Working on a selection',
      context: 'tag-dialog',
      i18n: {
        'de-AT': {
          title: 'Einen Tag wählen oder eingeben',
          body: 'Geben Sie einen neuen Tag ein oder klicken Sie auf einen Tag, den die Schlüssel in der Liste schon verwenden. Ein bestehender Tag verhindert ein Beinahe-Duplikat durch einen Tippfehler; „Add tag“ wendet ihn an.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite ili upišite tag',
          body: 'Upišite novi tag ili kliknite na neki koji ključevi u listi već koriste. Ponovna upotreba postojećeg taga sprečava skoro-duplikat nastao zbog greške u kucanju; „Add tag“ ga primenjuje.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk-qa"]',
      title: 'Check the selection',
      body: 'Opens the QA dialog limited to the selected keys. Nothing is written; clicking a finding there opens that key.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Die Auswahl prüfen',
          body: 'Öffnet den QA-Dialog nur für die ausgewählten Schlüssel. Es wird nichts geschrieben; ein Klick auf einen Befund öffnet den betreffenden Schlüssel.',
        },
        'sr-Latn-RS': {
          title: 'Proverite izbor',
          body: 'Otvara QA dijalog ograničen na izabrane ključeve. Ništa se ne upisuje; klik na nalaz otvara taj ključ.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk-export"]',
      title: 'Export the selection',
      body: 'Opens the Export dialog with its third option available: a working file of just the selected keys, empty values included, for handing work to somebody.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Die Auswahl exportieren',
          body: 'Öffnet den Dialog „Export“ mit der dritten Option: eine Arbeitsdatei nur mit den ausgewählten Schlüsseln, leere Werte inklusive, um Arbeit an jemanden weiterzugeben.',
        },
        'sr-Latn-RS': {
          title: 'Izvezite izbor',
          body: 'Otvara dijalog „Export“ sa dostupnom trećom opcijom: radni fajl samo sa izabranim ključevima, uključujući prazne vrednosti, da biste posao predali nekome.',
        },
      },
    },
    {
      selector: '[data-help="keys-bulk-delete"]',
      title: 'Delete the selected keys',
      body: 'Asks for confirmation, then deletes the selected keys. They leave every export at the next publish; translations and history stay in the database, so an administrator can still recover them. Keys other projects link are kept back, and a second confirmation names those projects before they are deleted too. Owners and developers only.',
      section: 'Working on a selection',
      context: 'selection',
      i18n: {
        'de-AT': {
          title: 'Die ausgewählten Schlüssel löschen',
          body: 'Fragt nach einer Bestätigung und löscht dann die ausgewählten Schlüssel. Sie fallen bei der nächsten Veröffentlichung aus jedem Export; Übersetzungen und Verlauf bleiben in der Datenbank, ein Administrator kann sie also wiederherstellen. Schlüssel, die andere Projekte verknüpfen, werden zurückbehalten; eine zweite Bestätigung nennt diese Projekte, bevor auch sie gelöscht werden. Nur für Owner und Developer.',
        },
        'sr-Latn-RS': {
          title: 'Obrišite izabrane ključeve',
          body: 'Traži potvrdu, a zatim briše izabrane ključeve. Pri sledećem objavljivanju nestaju iz svakog izvoza; prevodi i istorija ostaju u bazi, pa ih administrator i dalje može vratiti. Ključevi koje povezuju drugi projekti se zadržavaju, a druga potvrda navodi te projekte pre nego što se i oni obrišu. Samo za owner i developer.',
        },
      },
    },

    // ----- Save conflicts -----
    {
      selector: '[data-help="keys-conflict-keep-mine"]',
      title: 'Keep your version',
      body: 'The dialog opens when somebody saved one of your cells while you were typing; nothing from your batch was written. “Keep mine” sends your text again on top of their version, which it then replaces.',
      section: 'Save conflicts',
      context: 'conflict-dialog',
      i18n: {
        'de-AT': {
          title: 'Ihre Fassung behalten',
          body: 'Der Dialog öffnet sich, wenn jemand eine Ihrer Zellen gespeichert hat, während Sie getippt haben; von Ihrem Stapel wurde nichts geschrieben. „Keep mine“ sendet Ihren Text erneut auf Basis der anderen Fassung, die er dann ersetzt.',
        },
        'sr-Latn-RS': {
          title: 'Zadržite svoju verziju',
          body: 'Dijalog se otvara kada je neko sačuvao neku od vaših ćelija dok ste kucali; ništa iz vaše grupe izmena nije upisano. „Keep mine“ ponovo šalje vaš tekst preko njihove verzije, koju on onda zamenjuje.',
        },
      },
    },
    {
      selector: '[data-help="keys-conflict-take-theirs"]',
      title: 'Take their version',
      body: 'Drops your unsaved edits for the keys in conflict and reloads the list, so you see what the other person saved. Use it when their change is the better one, or when you are unsure.',
      section: 'Save conflicts',
      context: 'conflict-dialog',
      i18n: {
        'de-AT': {
          title: 'Die andere Fassung übernehmen',
          body: 'Verwirft Ihre ungespeicherten Änderungen an den betroffenen Schlüsseln und lädt die Liste neu, damit Sie sehen, was die andere Person gespeichert hat. Nehmen Sie das, wenn deren Änderung die bessere ist oder Sie unsicher sind.',
        },
        'sr-Latn-RS': {
          title: 'Prihvatite njihovu verziju',
          body: 'Odbacuje vaše nesačuvane izmene za ključeve u konfliktu i ponovo učitava listu, da biste videli šta je druga osoba sačuvala. Koristite ovo kada je njihova izmena bolja ili kada niste sigurni.',
        },
      },
    },

    // ----- Key details -----
    {
      selector: '[data-help="keys-detail"]',
      title: 'See a key at a glance',
      body: 'The top of the panel shows the key name, its description and chips for plural, maximum length and tags. Every locale of the project is listed below it, the source locale first.',
      section: 'Key details',
      context: 'detail-drawer',
      i18n: {
        'de-AT': {
          title: 'Einen Schlüssel auf einen Blick sehen',
          body: 'Oben im Panel stehen der Schlüsselname, seine Beschreibung und Chips für Plural, Maximallänge und Tags. Darunter folgen alle Sprachen des Projekts, die Quellsprache zuerst.',
        },
        'sr-Latn-RS': {
          title: 'Pogledajte ključ na prvi pogled',
          body: 'Na vrhu panela su naziv ključa, njegov opis i oznake za množinu, maksimalnu dužinu i tagove. Ispod su navedeni svi jezici projekta, izvorni jezik prvi.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-owner"]',
      title: 'A key that belongs elsewhere',
      body: '“owned by …” names the project or package the key belongs to when it is not this project’s own. You can read and translate it here — the change is written there if you translate there, otherwise it is sent there as a suggestion — but renaming, deleting, reviewing and AI suggestions are left to the owner.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ein Schlüssel, der woanders hingehört',
          body: '„owned by …“ nennt das Projekt oder Paket, dem der Schlüssel gehört, wenn er nicht diesem Projekt gehört. Sie können ihn hier lesen und übersetzen – die Änderung wird dort geschrieben, wenn Sie dort übersetzen, sonst geht sie als Vorschlag dorthin –, Umbenennen, Löschen, Prüfen und KI-Vorschläge bleiben aber dem Owner überlassen.',
        },
        'sr-Latn-RS': {
          title: 'Ključ koji pripada drugome',
          body: '„owned by …“ navodi projekat ili paket kome ključ pripada kada nije sopstveni ključ ovog projekta. Ovde možete da ga čitate i prevodite – izmena se upisuje tamo ako tamo prevodite, inače odlazi tamo kao predlog – ali preimenovanje, brisanje, pregled i AI predlozi ostaju vlasniku.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-linked-by"]',
      title: 'Projects that use this key',
      body: 'Other projects that link this key and export it with this project’s text. A change here reaches their bundles at their next publish; renaming or deleting the key asks for confirmation first, because their code still asks for this name.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Projekte, die diesen Schlüssel verwenden',
          body: 'Andere Projekte, die diesen Schlüssel verknüpfen und ihn mit dem Text dieses Projekts exportieren. Eine Änderung hier erreicht ihre Bundles bei ihrem nächsten Veröffentlichen; Umbenennen oder Löschen des Schlüssels fragt zuerst nach einer Bestätigung, weil ihr Code noch diesen Namen anfragt.',
        },
        'sr-Latn-RS': {
          title: 'Projekti koji koriste ovaj ključ',
          body: 'Drugi projekti koji povezuju ovaj ključ i izvoze ga sa tekstom ovog projekta. Izmena ovde stiže u njihove bundle-ove pri njihovoj sledećoj objavi; preimenovanje ili brisanje ključa prvo traži potvrdu, jer njihov kod i dalje traži ovo ime.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-rename"]',
      title: 'Rename the key',
      body: 'Opens the Rename key dialog. The pencil is shown only to owners and developers of the project, and to administrators.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Schlüssel umbenennen',
          body: 'Öffnet den Dialog „Rename key“. Den Stift sehen nur Owner und Developer des Projekts sowie Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Preimenujte ključ',
          body: 'Otvara dijalog „Rename key“. Olovku vide samo owner i developer projekta, kao i administratori.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-delete"]',
      title: 'Delete the key',
      body: 'Deletes this key after a confirmation. It leaves every export at the next publish — including the bundles of projects that link it, which the confirmation names, and their links are removed; its translations and history stay in the database, so an administrator can reverse it. Shown only to owners, developers and administrators.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Schlüssel löschen',
          body: 'Löscht diesen Schlüssel nach einer Bestätigung. Er fällt bei der nächsten Veröffentlichung aus jedem Export – auch aus den Bundles der Projekte, die ihn verknüpfen; die Bestätigung nennt sie, und ihre Verknüpfungen werden entfernt. Übersetzungen und Verlauf bleiben in der Datenbank, ein Administrator kann das also rückgängig machen. Nur für Owner, Developer und Administratoren sichtbar.',
        },
        'sr-Latn-RS': {
          title: 'Obrišite ključ',
          body: 'Briše ovaj ključ posle potvrde. Pri sledećem objavljivanju nestaje iz svakog izvoza – i iz bundle-ova projekata koji ga povezuju, koje potvrda navodi, a njihove veze se uklanjaju; prevodi i istorija ostaju u bazi, pa administrator to može da poništi. Vidljivo samo za owner, developer i administratore.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-translations"]',
      title: 'Edit every locale of the key',
      body: 'One field per locale, with its status next to the locale code. Enter saves a field straight away and Escape undoes the edit — there is no separate save. Owners and translators can edit; for everyone else the values are read-only.',
      section: 'Key details',
      context: 'detail-drawer',
      i18n: {
        'de-AT': {
          title: 'Alle Sprachen des Schlüssels bearbeiten',
          body: 'Ein Feld pro Sprache, mit dem Status neben dem Sprachcode. Enter speichert ein Feld sofort, Escape macht die Änderung rückgängig — ein eigenes Speichern gibt es nicht. Owner und Translator können bearbeiten; für alle anderen sind die Werte schreibgeschützt.',
        },
        'sr-Latn-RS': {
          title: 'Uredite sve jezike ključa',
          body: 'Jedno polje po jeziku, sa statusom pored koda jezika. Enter odmah čuva polje, a Escape poništava izmenu — posebnog čuvanja nema. Uređivati mogu owner i translator; za sve ostale vrednosti su samo za čitanje.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-plural-form"]',
      title: 'Fill the plural forms',
      body: 'A plural key gets one field per plural form of each locale — one and other in English, one, few and other in Serbian or Croatian — as the export expands them; this panel is the only place to edit them. A form outside the locale’s set appears only when a value is already stored there, so it can be cleared.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Pluralformen ausfüllen',
          body: 'Ein Pluralschlüssel hat ein Feld pro Pluralform der jeweiligen Sprache — one und other im Englischen, one, few und other im Serbischen oder Kroatischen —, so wie der Export sie schreibt; nur in diesem Panel können Sie sie bearbeiten. Eine Form außerhalb der Sprache erscheint nur, wenn dort bereits ein Wert gespeichert ist, damit er sich löschen lässt.',
        },
        'sr-Latn-RS': {
          title: 'Popunite oblike množine',
          body: 'Ključ sa množinom ima po jedno polje za svaki oblik množine datog jezika — one i other za engleski, one, few i other za srpski ili hrvatski — onako kako ih izvoz upisuje; ovaj panel je jedino mesto gde ih možete urediti. Oblik van skupa tog jezika pojavljuje se samo ako u njemu već postoji vrednost, da bi mogla da se obriše.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-ai-suggest"]',
      title: 'Ask the AI for a proposal',
      body: 'Switched off for now: the button stays greyed out until AI translation is available. Puts an AI translation for this locale into the field without saving it. Press Enter to keep it — it is then saved as your own draft — or Escape to discard it. Offered to owners and translators, and not for the source locale.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die KI um einen Vorschlag bitten',
          body: 'Derzeit abgeschaltet: Die Schaltfläche bleibt ausgegraut, bis die KI-Übersetzung verfügbar ist. Setzt eine KI-Übersetzung für diese Sprache in das Feld, ohne sie zu speichern. Drücken Sie Enter, um sie zu übernehmen — sie wird dann als Ihr eigener „draft“ gespeichert — oder Escape, um sie zu verwerfen. Für Owner und Translator, nicht für die Quellsprache.',
        },
        'sr-Latn-RS': {
          title: 'Zatražite predlog od AI',
          body: 'Trenutno isključeno: dugme ostaje sivo dok AI prevod ne postane dostupan. Upisuje AI prevod za ovaj jezik u polje, bez čuvanja. Pritisnite Enter da ga zadržite — tada se čuva kao vaš „draft“ — ili Escape da ga odbacite. Dostupno za owner i translator, ali ne i za izvorni jezik.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-review"]',
      title: 'Mark one locale reviewed',
      body: 'Sets this locale’s value to reviewed — the status a publish target may require before it publishes. Offered to owners and translators, and not for the source locale.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Eine Sprache als geprüft markieren',
          body: 'Setzt den Wert dieser Sprache auf „reviewed“ — den Status, den ein Veröffentlichungsziel vor dem Veröffentlichen verlangen kann. Für Owner und Translator, nicht für die Quellsprache.',
        },
        'sr-Latn-RS': {
          title: 'Označite jedan jezik kao pregledan',
          body: 'Postavlja vrednost ovog jezika na „reviewed“ — status koji cilj objavljivanja može da zahteva pre objavljivanja. Dostupno za owner i translator, ali ne i za izvorni jezik.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-clear"]',
      title: 'Clear a value',
      body: 'Empties this locale’s value. An empty cell is left out of the export, so the application shows its fallback text there. Shown only when the locale has a value.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen Wert leeren',
          body: 'Leert den Wert dieser Sprache. Eine leere Zelle fehlt im Export, die Anwendung zeigt dort also ihren Ersatztext. Nur sichtbar, wenn die Sprache einen Wert hat.',
        },
        'sr-Latn-RS': {
          title: 'Obrišite vrednost',
          body: 'Prazni vrednost ovog jezika. Prazna ćelija se izostavlja iz izvoza, pa aplikacija tu prikazuje rezervni tekst. Vidljivo samo kada jezik ima vrednost.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-routes"]',
      title: 'See where the key is used',
      body: 'Lists the application routes on which the in-context editor saw this key, with how often. Shown only when a route has been reported.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Sehen, wo der Schlüssel verwendet wird',
          body: 'Listet die Routen der Anwendung, auf denen der In-Context-Editor diesen Schlüssel gesehen hat, samt Häufigkeit. Nur sichtbar, wenn eine Route gemeldet wurde.',
        },
        'sr-Latn-RS': {
          title: 'Pogledajte gde se ključ koristi',
          body: 'Navodi rute aplikacije na kojima je editor u kontekstu video ovaj ključ, i koliko puta. Vidljivo samo kada je neka ruta prijavljena.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-comments"]',
      title: 'Discuss the key',
      body: 'Write a question or a decision about the string and click “Comment”. Commenting needs only read access, so it is how anyone on the project raises a problem with a text.',
      section: 'Key details',
      context: 'detail-drawer',
      i18n: {
        'de-AT': {
          title: 'Über den Schlüssel diskutieren',
          body: 'Schreiben Sie eine Frage oder Entscheidung zum Text und klicken Sie auf „Comment“. Kommentieren braucht nur Lesezugriff — so kann jeder im Projekt ein Problem mit einem Text melden.',
        },
        'sr-Latn-RS': {
          title: 'Diskutujte o ključu',
          body: 'Napišite pitanje ili odluku o tekstu i kliknite na „Comment“. Za komentarisanje je dovoljan pristup za čitanje, pa tako svako na projektu može da prijavi problem sa tekstom.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-comment-about"]',
      title: 'Pin a comment to a locale',
      body: 'Choose a locale to attach the comment to that language — “this reads wrong in German” — or leave “The key” for the key as a whole. The locale then shows as a chip on the comment.',
      section: 'Key details',
      context: 'detail-drawer',
      i18n: {
        'de-AT': {
          title: 'Einen Kommentar einer Sprache zuordnen',
          body: 'Wählen Sie eine Sprache, um den Kommentar dieser Sprache zuzuordnen — „klingt auf Deutsch falsch“ — oder lassen Sie „The key“ für den ganzen Schlüssel. Die Sprache erscheint dann als Chip am Kommentar.',
        },
        'sr-Latn-RS': {
          title: 'Vežite komentar za jezik',
          body: 'Izaberite jezik da biste komentar vezali za taj jezik — „na nemačkom zvuči pogrešno“ — ili ostavite „The key“ za ključ u celini. Jezik se zatim prikazuje kao oznaka na komentaru.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-screenshots"]',
      title: 'Look at screenshots',
      body: 'Shows the screenshots attached to this key, which give translators the context a key name cannot. Shown only when the key has any.',
      section: 'Key details',
      context: 'detail-drawer',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Screenshots ansehen',
          body: 'Zeigt die Screenshots zu diesem Schlüssel, die Übersetzern den Kontext geben, den ein Schlüsselname nicht liefert. Nur sichtbar, wenn der Schlüssel welche hat.',
        },
        'sr-Latn-RS': {
          title: 'Pogledajte snimke ekrana',
          body: 'Prikazuje snimke ekrana priložene uz ovaj ključ, koji prevodiocima daju kontekst koji naziv ključa ne može. Vidljivo samo kada ključ ima snimke.',
        },
      },
    },
    {
      selector: '[data-help="keys-detail-history"]',
      title: 'Trace the changes',
      body: 'The last twenty changes to this key: locale and plural form, the status it got, where the change came from, who made it and when, and the value. Use it to see who wrote a translation, or what it said before.',
      section: 'Key details',
      context: 'detail-drawer',
      i18n: {
        'de-AT': {
          title: 'Die Änderungen nachverfolgen',
          body: 'Die letzten zwanzig Änderungen an diesem Schlüssel: Sprache und Pluralform, der neue Status, die Herkunft der Änderung, wer sie wann gemacht hat und der Wert. So sehen Sie, wer eine Übersetzung geschrieben hat oder was vorher dastand.',
        },
        'sr-Latn-RS': {
          title: 'Pratite izmene',
          body: 'Poslednjih dvadeset izmena ovog ključa: jezik i oblik množine, status koji je dobio, odakle je izmena došla, ko ju je napravio i kada, i vrednost. Tako vidite ko je napisao prevod ili šta je ranije pisalo.',
        },
      },
    },

    // ----- New key -----
    {
      selector: '[data-help="keys-new-name"]',
      title: 'Name the key',
      body: 'Letters, digits, underscore, dash, & and @, separated by dots — for example rep.documents.download. Letters with diacritics are allowed, as in LocalCoverage.Kärnten, and so are spaces inside the name, as in Company.GrECo LV, but not at its start or end. The first segment decides the owner, so it has to be a prefix this project owns; any other prefix is refused.',
      section: 'New key',
      context: 'new-key-dialog',
      i18n: {
        'de-AT': {
          title: 'Den Schlüssel benennen',
          body: 'Buchstaben, Ziffern, Unterstrich, Bindestrich, & und @, durch Punkte getrennt — etwa rep.documents.download. Buchstaben mit diakritischen Zeichen sind erlaubt, wie in LocalCoverage.Kärnten, ebenso Leerzeichen innerhalb des Namens, wie in Company.GrECo LV, aber nicht an seinem Anfang oder Ende. Das erste Segment bestimmt den Eigentümer, es muss also ein Präfix sein, das diesem Projekt gehört; jedes andere Präfix wird abgelehnt.',
        },
        'sr-Latn-RS': {
          title: 'Imenujte ključ',
          body: 'Slova, cifre, donja crta, crtica, & i @, razdvojeni tačkama — na primer rep.documents.download. Dozvoljena su i slova sa dijakriticima, kao u LocalCoverage.Kärnten, i razmaci unutar imena, kao u Company.GrECo LV, ali ne na njegovom početku ili kraju. Prvi segment određuje vlasnika, pa mora biti prefiks koji pripada ovom projektu; svaki drugi prefiks se odbija.',
        },
      },
    },
    {
      selector: '[data-help="keys-new-description"]',
      title: 'Describe the context',
      body: 'Context for translators and for the AI, shown on the key in the grid and at the top of its details. Worth writing for anything ambiguous, such as a single word that could be a verb or a noun.',
      section: 'New key',
      context: 'new-key-dialog',
      i18n: {
        'de-AT': {
          title: 'Den Kontext beschreiben',
          body: 'Kontext für Übersetzer und für die KI, sichtbar am Schlüssel im Raster und oben in seinen Details. Lohnt sich bei allem Mehrdeutigen, etwa einem einzelnen Wort, das Verb oder Substantiv sein kann.',
        },
        'sr-Latn-RS': {
          title: 'Opišite kontekst',
          body: 'Kontekst za prevodioce i za AI, vidljiv na ključu u tabeli i na vrhu njegovih detalja. Vredi ga napisati za sve što je dvosmisleno, na primer jednu reč koja može biti glagol ili imenica.',
        },
      },
    },
    {
      selector: '[data-help="keys-new-plural"]',
      title: 'Make it a plural key',
      body: 'Switch it on for text that changes with a number (“1 file”, “5 files”). The source value field then disappears: plural forms are filled in the key details, and the grid shows the key read-only.',
      section: 'New key',
      context: 'new-key-dialog',
      i18n: {
        'de-AT': {
          title: 'Einen Pluralschlüssel anlegen',
          body: 'Schalten Sie das für Text ein, der sich mit einer Zahl ändert („1 Datei“, „5 Dateien“). Das Feld für den Quellwert verschwindet dann: Pluralformen werden in den Schlüsseldetails ausgefüllt, und das Raster zeigt den Schlüssel schreibgeschützt.',
        },
        'sr-Latn-RS': {
          title: 'Napravite ključ sa množinom',
          body: 'Uključite ovo za tekst koji se menja sa brojem („1 fajl“, „5 fajlova“). Polje za izvornu vrednost tada nestaje: oblici množine popunjavaju se u detaljima ključa, a tabela prikazuje ključ samo za čitanje.',
        },
      },
    },
    {
      selector: '[data-help="keys-new-source"]',
      title: 'Enter the source text',
      body: 'The text in the project’s source locale. Without it the key is not exported in any locale, which is why it is part of the form. Click “Create” to save the key.',
      section: 'New key',
      context: 'new-key-dialog',
      i18n: {
        'de-AT': {
          title: 'Den Quelltext eingeben',
          body: 'Der Text in der Quellsprache des Projekts. Ohne ihn wird der Schlüssel in keiner Sprache exportiert, deshalb gehört er zum Formular. Klicken Sie auf „Create“, um den Schlüssel zu speichern.',
        },
        'sr-Latn-RS': {
          title: 'Unesite izvorni tekst',
          body: 'Tekst na izvornom jeziku projekta. Bez njega se ključ ne izvozi ni na jednom jeziku, zato je deo obrasca. Kliknite na „Create“ da biste sačuvali ključ.',
        },
      },
    },

    // ----- Rename a key -----
    {
      selector: '[data-help="keys-rename-name"]',
      title: 'Enter the new name',
      body: 'Same rules as a new key name. Changing the first segment moves the key to whoever owns that prefix, and the server refuses it unless the prefix is registered and you may write there. “Rename” stays disabled until the name differs.',
      section: 'Rename a key',
      context: 'rename-dialog',
      i18n: {
        'de-AT': {
          title: 'Den neuen Namen eingeben',
          body: 'Es gelten dieselben Regeln wie für einen neuen Schlüsselnamen. Ändert sich das erste Segment, wandert der Schlüssel zum Eigentümer dieses Präfixes; der Server lehnt das ab, außer das Präfix ist registriert und Sie dürfen dort schreiben. „Rename“ bleibt deaktiviert, bis sich der Name unterscheidet.',
        },
        'sr-Latn-RS': {
          title: 'Unesite novi naziv',
          body: 'Važe ista pravila kao za naziv novog ključa. Promena prvog segmenta premešta ključ vlasniku tog prefiksa, a server to odbija osim ako je prefiks registrovan i vi tamo smete da pišete. „Rename“ ostaje onemogućeno dok se naziv ne promeni.',
        },
      },
    },
    {
      selector: '[data-help="keys-rename-linked"]',
      title: 'Renaming a key other projects use',
      body: 'Shown when other projects link the key. Their code asks for the current name, and the rename changes the name in their bundles too, so their screens fall back until they ask for the new one. The button then reads “Rename anyway”.',
      section: 'Rename a key',
      context: 'rename-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen Schlüssel umbenennen, den andere Projekte verwenden',
          body: 'Erscheint, wenn andere Projekte den Schlüssel verknüpfen. Ihr Code fragt den aktuellen Namen an, und das Umbenennen ändert den Namen auch in ihren Bundles; ihre Bildschirme greifen daher auf den Fallback zurück, bis sie den neuen Namen anfragen. Die Schaltfläche lautet dann „Rename anyway“.',
        },
        'sr-Latn-RS': {
          title: 'Preimenovanje ključa koji koriste drugi projekti',
          body: 'Prikazuje se kada drugi projekti povezuju ključ. Njihov kod traži trenutno ime, a preimenovanje menja ime i u njihovim bundle-ovima, pa njihovi ekrani prelaze na rezervni tekst dok ne počnu da traže novo ime. Dugme tada glasi „Rename anyway“.',
        },
      },
    },
    {
      selector: '[data-help="keys-rename-alias"]',
      title: 'Keep the old name working',
      body: 'Ticked by default: builds already shipped keep finding the string under the old name for 30 days. Untick it only when nothing uses the old name any more — every build that still asks for it loses the string immediately.',
      section: 'Rename a key',
      context: 'rename-dialog',
      i18n: {
        'de-AT': {
          title: 'Den alten Namen weiter gelten lassen',
          body: 'Standardmäßig angehakt: bereits ausgelieferte Builds finden den Text noch 30 Tage unter dem alten Namen. Entfernen Sie den Haken nur, wenn nichts mehr den alten Namen verwendet — jeder Build, der noch danach fragt, verliert den Text sofort.',
        },
        'sr-Latn-RS': {
          title: 'Zadržite stari naziv u upotrebi',
          body: 'Podrazumevano označeno: već isporučene verzije aplikacije još 30 dana nalaze tekst pod starim nazivom. Uklonite oznaku samo kada stari naziv više niko ne koristi — svaka verzija koja ga još traži odmah gubi tekst.',
        },
      },
    },

    // ----- QA -----
    {
      selector: '[data-help="keys-qa-locale"]',
      title: 'Choose the locale to check',
      body: 'QA runs one locale at a time, because its findings are per value. The list offers the locale columns you are showing, or every locale of the project when none is shown.',
      section: 'QA',
      context: 'qa-dialog',
      i18n: {
        'de-AT': {
          title: 'Die zu prüfende Sprache wählen',
          body: 'QA prüft eine Sprache nach der anderen, weil die Befunde pro Wert gelten. Die Liste bietet die eingeblendeten Sprachspalten an, oder alle Sprachen des Projekts, wenn keine eingeblendet ist.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite jezik za proveru',
          body: 'QA proverava jedan po jedan jezik, jer se nalazi odnose na pojedinačne vrednosti. Lista nudi kolone jezika koje prikazujete, ili sve jezike projekta kada nijedna nije prikazana.',
        },
      },
    },
    {
      selector: '[data-help="keys-qa-run"]',
      title: 'Run the check',
      body: 'Looks for placeholders that do not match the source, missing plural forms and values far longer than the source. Nothing is written, so it is safe to run on the whole project.',
      section: 'QA',
      context: 'qa-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Prüfung starten',
          body: 'Sucht nach Platzhaltern, die nicht zum Quelltext passen, fehlenden Pluralformen und Werten, die viel länger sind als der Quelltext. Es wird nichts geschrieben, Sie können es also gefahrlos auf das ganze Projekt anwenden.',
        },
        'sr-Latn-RS': {
          title: 'Pokrenite proveru',
          body: 'Traži placeholdere koji se ne poklapaju sa izvornim tekstom, oblike množine koji nedostaju i vrednosti mnogo duže od izvornog teksta. Ništa se ne upisuje, pa je bezbedno pokrenuti je na celom projektu.',
        },
      },
    },
    {
      selector: '[data-help="keys-qa-findings"]',
      title: 'Work through the findings',
      body: 'Each finding names the key, the locale and the rule it broke; the chips above count errors and warnings. An error blocks a strict save and any publish to a target that requires QA-clean, a warning is advice. Click a key name to close the dialog and open that key.',
      section: 'QA',
      context: 'qa-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Befunde abarbeiten',
          body: 'Jeder Befund nennt Schlüssel, Sprache und die verletzte Regel; die Chips darüber zählen Fehler und Warnungen. Ein Fehler blockiert ein strenges Speichern und jede Veröffentlichung auf ein Ziel, das QA-Fehlerfreiheit verlangt, eine Warnung ist ein Hinweis. Ein Klick auf einen Schlüsselnamen schließt den Dialog und öffnet den Schlüssel.',
        },
        'sr-Latn-RS': {
          title: 'Rešavajte nalaze',
          body: 'Svaki nalaz navodi ključ, jezik i pravilo koje je prekršeno; oznake iznad broje greške i upozorenja. Greška blokira strogo čuvanje i svako objavljivanje na cilj koji zahteva QA bez grešaka, a upozorenje je savet. Kliknite na naziv ključa da zatvorite dijalog i otvorite taj ključ.',
        },
      },
    },

    // ----- Export -----
    {
      selector: '[data-help="keys-export-mode"]',
      title: 'Choose what to download',
      body: '“One locale, as published” gives one .json file, “Every locale” a .zip — exactly what publish uploads, so keys without a source value and untranslated cells are absent, and plural keys appear as key_one, key_other and so on. The third option, enabled once rows are selected, is a working file of those keys with empty values included, to be filled in and imported back.',
      section: 'Export',
      context: 'export-dialog',
      i18n: {
        'de-AT': {
          title: 'Wählen, was heruntergeladen wird',
          body: '„One locale, as published“ liefert eine .json-Datei, „Every locale“ ein .zip — genau das, was die Veröffentlichung hochlädt: Schlüssel ohne Quellwert und unübersetzte Zellen fehlen, Pluralschlüssel erscheinen als key_one, key_other usw. Die dritte Option, aktiv sobald Zeilen ausgewählt sind, ist eine Arbeitsdatei dieser Schlüssel samt leeren Werten, zum Ausfüllen und Zurückimportieren.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite šta preuzimate',
          body: '„One locale, as published“ daje jedan .json fajl, a „Every locale“ .zip — tačno ono što objavljivanje otprema: ključevi bez izvorne vrednosti i neprevedene ćelije izostaju, a ključevi sa množinom pojavljuju se kao key_one, key_other i tako dalje. Treća opcija, dostupna kada su redovi izabrani, jeste radni fajl tih ključeva uključujući prazne vrednosti, za popunjavanje i ponovni uvoz.',
        },
      },
    },
    {
      selector: '[data-help="keys-export-from"]',
      title: 'Export the draft or a release',
      body: '“The current draft” is what is in the editor now. “The last release of a target” is what that publish target last published — the files the applications reading that target receive.',
      section: 'Export',
      context: 'export-dialog',
      i18n: {
        'de-AT': {
          title: 'Entwurf oder Release exportieren',
          body: '„The current draft“ ist der aktuelle Stand im Editor. „The last release of a target“ ist das, was dieses Veröffentlichungsziel zuletzt veröffentlicht hat — die Dateien, die Anwendungen mit diesem Ziel erhalten.',
        },
        'sr-Latn-RS': {
          title: 'Izvezite nacrt ili izdanje',
          body: '„The current draft“ je ono što je trenutno u editoru. „The last release of a target“ je ono što je taj cilj objavljivanja poslednji put objavio — fajlovi koje dobijaju aplikacije koje čitaju taj cilj.',
        },
      },
    },
    {
      selector: '[data-help="keys-export-target"]',
      title: 'Pick the publish target',
      body: 'Which target’s last published files to download. “Download” stays disabled until a target is chosen.',
      section: 'Export',
      context: 'export-release',
      i18n: {
        'de-AT': {
          title: 'Das Veröffentlichungsziel wählen',
          body: 'Von welchem Ziel die zuletzt veröffentlichten Dateien heruntergeladen werden. „Download“ bleibt deaktiviert, bis ein Ziel gewählt ist.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite cilj objavljivanja',
          body: 'Sa kog cilja se preuzimaju poslednji objavljeni fajlovi. „Download“ ostaje onemogućeno dok se cilj ne izabere.',
        },
      },
    },
    {
      selector: '[data-help="keys-export-locale"]',
      title: 'Pick the locale',
      body: 'The locale of the .json file, with the source locale marked. It is not asked for the .zip, which holds every locale; click “Download” to save the file.',
      section: 'Export',
      context: 'export-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Sprache wählen',
          body: 'Die Sprache der .json-Datei; die Quellsprache ist gekennzeichnet. Beim .zip wird nicht gefragt, es enthält alle Sprachen; klicken Sie auf „Download“, um die Datei zu speichern.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite jezik',
          body: 'Jezik .json fajla, sa označenim izvornim jezikom. Za .zip se ne bira, jer sadrži sve jezike; kliknite na „Download“ da sačuvate fajl.',
        },
      },
    },

    // ----- AI translate -----
    {
      selector: '[data-help="keys-ai-locales"]',
      title: 'Choose the locales to fill',
      body: 'The locales the job writes into; it starts with the locale columns you are showing. Leave the list empty to translate into every locale of the project except the source. A translator limited to certain locales can only start a job for those.',
      section: 'AI translate',
      context: 'ai-dialog',
      i18n: {
        'de-AT': {
          title: 'Die zu füllenden Sprachen wählen',
          body: 'Die Sprachen, in die der Auftrag schreibt; vorausgewählt sind die eingeblendeten Sprachspalten. Lassen Sie die Liste leer, um in alle Sprachen des Projekts außer der Quellsprache zu übersetzen. Ein Translator, der auf bestimmte Sprachen beschränkt ist, kann nur für diese einen Auftrag starten.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite jezike za popunjavanje',
          body: 'Jezici u koje posao upisuje; unapred su izabrane prikazane kolone jezika. Ostavite listu praznom da biste preveli na sve jezike projekta osim izvornog. Translator ograničen na određene jezike može da pokrene posao samo za njih.',
        },
      },
    },
    {
      selector: '[data-help="keys-ai-only-empty"]',
      title: 'Ask only about empty cells',
      body: 'On by default: cells the job may not overwrite are not sent to the provider at all, which saves cost. Switching it off does not let the job overwrite anything more — that is decided by the next switch.',
      section: 'AI translate',
      context: 'ai-dialog',
      i18n: {
        'de-AT': {
          title: 'Nur nach leeren Zellen fragen',
          body: 'Standardmäßig ein: Zellen, die der Auftrag nicht überschreiben darf, werden gar nicht an den Anbieter geschickt, das spart Kosten. Ausschalten erlaubt dem Auftrag nicht, mehr zu überschreiben — das entscheidet der nächste Schalter.',
        },
        'sr-Latn-RS': {
          title: 'Pitajte samo za prazne ćelije',
          body: 'Podrazumevano uključeno: ćelije koje posao ne sme da prepiše uopšte se ne šalju provajderu, što štedi troškove. Isključivanje ne dozvoljava poslu da prepiše više — o tome odlučuje sledeći prekidač.',
        },
      },
    },
    {
      selector: '[data-help="keys-ai-overwrite-machine"]',
      title: 'Replace earlier AI output',
      body: 'Lets the job replace values a previous AI run produced. Draft and reviewed translations are never overwritten, whatever the switches say.',
      section: 'AI translate',
      context: 'ai-dialog',
      i18n: {
        'de-AT': {
          title: 'Frühere KI-Ergebnisse ersetzen',
          body: 'Erlaubt dem Auftrag, Werte aus einem früheren KI-Lauf zu ersetzen. Übersetzungen im Status „draft“ oder „reviewed“ werden nie überschrieben, egal wie die Schalter stehen.',
        },
        'sr-Latn-RS': {
          title: 'Zamenite raniji AI rezultat',
          body: 'Dozvoljava poslu da zameni vrednosti koje je proizveo raniji AI prevod. Prevodi sa statusom „draft“ ili „reviewed“ nikada se ne prepisuju, bez obzira na prekidače.',
        },
      },
    },
    {
      selector: '[data-help="keys-ai-start"]',
      title: 'Start the translation',
      body: 'Starts a background job and shows its progress. Values are written as machine translations; a suggestion that fails QA, such as a changed placeholder, is counted as failed instead of written. Disabled when this environment has no AI provider configured.',
      section: 'AI translate',
      context: 'ai-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Übersetzung starten',
          body: 'Startet einen Hintergrundauftrag und zeigt seinen Fortschritt. Werte werden als maschinelle Übersetzungen geschrieben; ein Vorschlag, der die QA nicht besteht, etwa mit verändertem Platzhalter, zählt als fehlgeschlagen statt geschrieben zu werden. Deaktiviert, wenn in dieser Umgebung kein KI-Anbieter konfiguriert ist.',
        },
        'sr-Latn-RS': {
          title: 'Pokrenite prevod',
          body: 'Pokreće posao u pozadini i prikazuje njegov napredak. Vrednosti se upisuju kao mašinski prevodi; predlog koji ne prođe QA, na primer sa izmenjenim placeholderom, broji se kao neuspeo umesto da bude upisan. Onemogućeno kada u ovom okruženju nije podešen AI provajder.',
        },
      },
    },
    {
      selector: '[data-help="keys-ai-progress"]',
      title: 'Follow the job',
      body: 'Shows done and failed cells out of the total; failed cells stay empty because the provider was unsure or QA refused the value. “Run in background” closes the dialog while the job carries on, “Done” closes it when the job has finished — either one reloads the grid.',
      section: 'AI translate',
      context: 'ai-running',
      i18n: {
        'de-AT': {
          title: 'Den Auftrag verfolgen',
          body: 'Zeigt erledigte und fehlgeschlagene Zellen im Verhältnis zur Gesamtzahl; fehlgeschlagene Zellen bleiben leer, weil der Anbieter unsicher war oder die QA den Wert abgelehnt hat. „Run in background“ schließt den Dialog, während der Auftrag weiterläuft, „Done“ schließt ihn nach dem Ende — beides lädt das Raster neu.',
        },
        'sr-Latn-RS': {
          title: 'Pratite posao',
          body: 'Prikazuje završene i neuspele ćelije od ukupnog broja; neuspele ćelije ostaju prazne jer provajder nije bio siguran ili je QA odbio vrednost. „Run in background“ zatvara dijalog dok posao nastavlja, „Done“ ga zatvara kada se posao završi — oba ponovo učitavaju tabelu.',
        },
      },
    },
    {
      selector: '[data-help="keys-ai-stop"]',
      title: 'Stop the job',
      body: 'Stops the job after the current batch, so a few more cells may still be written. What is already written stays written.',
      section: 'AI translate',
      context: 'ai-running',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Auftrag stoppen',
          body: 'Stoppt den Auftrag nach dem laufenden Stapel, es können also noch ein paar Zellen geschrieben werden. Was schon geschrieben ist, bleibt.',
        },
        'sr-Latn-RS': {
          title: 'Zaustavite posao',
          body: 'Zaustavlja posao posle tekuće grupe, pa se još nekoliko ćelija može upisati. Ono što je već upisano ostaje.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Schlüssel',
      intro:
        'Der Editor eines Projekts: eine Zeile pro Schlüssel, die Quellsprache plus bis zu sechs Sprachspalten und direkte Bearbeitung, die laufend speichert. Releases veröffentlichen, was hier geschrieben wird.',
      sections: {
        'Getting around': 'Überblick',
        'Finding a key': 'Einen Schlüssel finden',
        'Reading the grid': 'Das Raster lesen',
        'Working on a selection': 'Mit einer Auswahl arbeiten',
        'Save conflicts': 'Speicherkonflikte',
        'Key details': 'Schlüsseldetails',
        'New key': 'Neuer Schlüssel',
        'Rename a key': 'Einen Schlüssel umbenennen',
        QA: 'QA',
        Export: 'Export',
        'AI translate': 'KI-Übersetzung',
      },
      contexts: {
        selection: 'Haken Sie das Kästchen am Anfang einer oder mehrerer Zeilen an.',
        'tag-dialog': 'Wählen Sie Zeilen aus und klicken Sie in der Auswahlleiste auf „Add tag“.',
        'conflict-dialog':
          'Öffnet sich von selbst, wenn ein Speichern mit einem Wert kollidiert, den jemand anderer vorher gespeichert hat.',
        'detail-drawer': 'Klicken Sie im Raster auf einen Schlüsselnamen.',
        'status-filter': 'Wählen Sie zuerst einen Wert im Filter „Status“.',
        'rename-dialog': 'Öffnen Sie einen Schlüssel und klicken Sie auf das Stiftsymbol neben seinem Namen.',
        'new-key-dialog': 'Klicken Sie rechts oben auf „New key“.',
        'qa-dialog': 'Klicken Sie oben auf „QA“ oder in der Auswahlleiste auf „Run QA“.',
        'export-dialog': 'Klicken Sie oben auf „Export“ oder in der Auswahlleiste auf „Export selection“.',
        'export-release': 'Stellen Sie im Dialog „Export“ das Feld „From“ auf „The last release of a target“.',
        'ai-dialog': 'Klicken Sie oben auf „AI translate all“ oder in der Auswahlleiste auf „AI translate“.',
        'ai-running': 'Klicken Sie im Dialog „AI translate“ auf „Translate“.',
      },
      tips: [
        'Was Sie ändern dürfen, hängt von Ihrer Rolle in diesem Projekt ab. Owner und Translator bearbeiten Werte und markieren sie als „reviewed“; Owner und Developer legen Schlüssel an, benennen sie um, taggen und löschen sie; Administratoren dürfen alles. Kommentieren darf jeder, der das Projekt öffnen kann.',
        'Ein Schlüssel ohne Wert in der Quellsprache wird in keiner Sprache exportiert, und eine unübersetzte Zelle fehlt in der veröffentlichten Datei — die Anwendung zeigt dort ihren Ersatztext.',
        'Meldet ein Speichern, dass eine Änderung zu einem Vorschlag wurde, gehört der Schlüssel jemand anderem: einem gemeinsamen Paket, dessen Owner Sie nicht sind, oder einem anderen Projekt, dessen Schlüssel dieses Projekt verknüpft und in dem Sie nicht übersetzen. Dieser Owner entscheidet darüber; direkt geschrieben wurde nichts.',
        'Schlüssel, die dieses Projekt aus anderen Projekten verknüpft, erscheinen mit dem Code ihres Owners neben dem Namen, sobald eine Verknüpfung unter „Linked keys“ oder aus dem Eingang „Missing keys“ angelegt wurde; mit ?includeLinked=0 in der Adresse blenden Sie sie aus. Ihre Zeilen lassen sich nicht anhaken, weil Löschen, Taggen und Prüfen eines Schlüssels Sache seines Owners sind.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Ključevi',
      intro:
        'Editor jednog projekta: jedan red po ključu, izvorni jezik i do šest kolona jezika, uz direktno uređivanje koje čuva usput. Izdanja objavljuju ono što je ovde napisano.',
      sections: {
        'Getting around': 'Snalaženje',
        'Finding a key': 'Pronalaženje ključa',
        'Reading the grid': 'Čitanje tabele',
        'Working on a selection': 'Rad sa izborom',
        'Save conflicts': 'Konflikti pri čuvanju',
        'Key details': 'Detalji ključa',
        'New key': 'Novi ključ',
        'Rename a key': 'Preimenovanje ključa',
        QA: 'QA',
        Export: 'Izvoz',
        'AI translate': 'AI prevod',
      },
      contexts: {
        selection: 'Označite polje za potvrdu na početku jednog ili više redova.',
        'tag-dialog': 'Izaberite redove, pa u traci izbora kliknite na „Add tag“.',
        'conflict-dialog':
          'Otvara se sam kada se čuvanje sudari sa vrednošću koju je neko drugi sačuvao pre vas.',
        'detail-drawer': 'Kliknite na naziv ključa u tabeli.',
        'status-filter': 'Prvo izaberite vrednost u filteru „Status“.',
        'rename-dialog': 'Otvorite ključ i kliknite na ikonicu olovke pored njegovog naziva.',
        'new-key-dialog': 'Kliknite na „New key“ gore desno.',
        'qa-dialog': 'Kliknite na „QA“ na vrhu ili na „Run QA“ u traci izbora.',
        'export-dialog': 'Kliknite na „Export“ na vrhu ili na „Export selection“ u traci izbora.',
        'export-release': 'U dijalogu „Export“ postavite „From“ na „The last release of a target“.',
        'ai-dialog': 'Kliknite na „AI translate all“ na vrhu ili na „AI translate“ u traci izbora.',
        'ai-running': 'Kliknite na „Translate“ u dijalogu „AI translate“.',
      },
      tips: [
        'Šta smete da menjate zavisi od vaše uloge u ovom projektu. Owner i translator uređuju vrednosti i označavaju ih kao „reviewed“; owner i developer kreiraju, preimenuju, taguju i brišu ključeve; administratori smeju sve. Komentarisati može svako ko može da otvori projekat.',
        'Ključ bez vrednosti na izvornom jeziku ne izvozi se ni na jednom jeziku, a neprevedena ćelija izostaje iz objavljenog fajla — aplikacija tu prikazuje svoj rezervni tekst.',
        'Ako vam čuvanje javi da je izmena postala predlog, ključ pripada nekom drugom: deljenom paketu čiji niste owner, ili drugom projektu čiji ključ ovaj projekat povezuje, a u kome ne prevodite. O njemu odlučuje taj vlasnik; ništa nije direktno upisano.',
        'Ključevi koje ovaj projekat povezuje iz drugih projekata prikazuju se sa kodom vlasnika pored imena, kada se veza napravi na „Linked keys“ ili iz inbox-a „Missing keys“; dodajte ?includeLinked=0 u adresu da ih sakrijete. Njihovi redovi ne mogu da se označe, jer su brisanje, tagovanje i pregled ključa posao njegovog vlasnika.',
      ],
    },
  },
};
